<script setup lang="ts">
import { nextTick, onMounted, watch } from 'vue'
import { markRouteRendered, routeLoading, type RouteLoadingState } from '../router/route-loading'

const props = withDefaults(defineProps<{ state?: RouteLoadingState }>(), {
  state: () => routeLoading,
})

async function markFirstRender(path: string | null) {
  if (!path) return
  // A completed navigation precedes RouterView's update. Let Vue flush that
  // update before removing the first-load status, without changing route keys.
  await nextTick()
  markRouteRendered(props.state, path)
}

watch(() => props.state.resolvedPath, markFirstRender, { flush: 'post' })
onMounted(() => { void markFirstRender(props.state.resolvedPath) })

function reloadPage() {
  window.location.reload()
}
</script>

<template>
  <div
    v-if="state.phase === 'loading' && !state.renderedPath"
    role="status"
    aria-live="polite"
    class="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-primary-50 px-6 text-center text-primary-800"
  >
    <div>
      <p class="text-lg font-semibold">Opening ClaimShield…</p>
      <p class="mt-2 text-sm">Loading page</p>
    </div>
  </div>
  <div
    v-if="state.phase === 'error'"
    role="alert"
    :class="state.renderedPath
      ? 'fixed inset-x-0 top-0 z-50 bg-primary-700 px-4 py-3 text-center text-white'
      : 'fixed inset-0 z-50 flex min-h-screen flex-col items-center justify-center bg-primary-50 px-6 text-center text-primary-800'"
  >
    <p>We could not load this page.</p>
    <button type="button" class="mt-2 underline" @click="reloadPage">Reload page</button>
  </div>
  <RouterView />
</template>
